// V7.9 - Sync Manager
// Implements outbox pattern: queue mutations offline, auto-sync when online
// Does NOT alter any existing functionality — purely additive layer

import {
  getOutboxItems,
  markOutboxSynced,
  updateOutboxAttempt,
  getUnsyncedEvents,
  markEventSynced,
  getUnsyncedImages,
  markImageSynced,
  addSyncLog,
  generateIdempotencyKey,
  type OutboxItem,
  type OfflineEvent,
  type OfflineImage,
} from './offlineStore';

type SyncStatus = 'idle' | 'syncing' | 'error';
type ConnectionStatus = 'online' | 'offline' | 'syncing';

let syncStatus: SyncStatus = 'idle';
let connectionStatus: ConnectionStatus = navigator.onLine ? 'online' : 'offline';
const listeners: Set<(status: ConnectionStatus) => void> = new Set();

// ============================================================================
// Connection status management
// ============================================================================

export function getConnectionStatus(): ConnectionStatus {
  return connectionStatus;
}

export function onConnectionStatusChange(callback: (status: ConnectionStatus) => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function setConnectionStatus(status: ConnectionStatus) {
  connectionStatus = status;
  listeners.forEach((cb) => {
    try { cb(status); } catch (e) { console.error('[Sync] Listener error:', e); }
  });
}

// ============================================================================
// Online/Offline detection
// ============================================================================

export function initSyncManager() {
  // Listen for online/offline events
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Also poll connectivity every 30 seconds as fallback
  setInterval(checkConnectivity, 30000);

  // Listen for SW sync messages
  window.addEventListener('ella:sync-events', () => syncAll());
  window.addEventListener('ella:sync-images', () => syncAll());

  // Initial status
  if (navigator.onLine) {
    setConnectionStatus('online');
  } else {
    setConnectionStatus('offline');
  }

  console.log('[Sync] Manager initialized, status:', connectionStatus);
}

function handleOnline() {
  console.log('[Sync] Connection restored');
  setConnectionStatus('online');
  // Auto-sync after a short delay to let connection stabilize
  setTimeout(() => syncAll(), 2000);
}

function handleOffline() {
  console.log('[Sync] Connection lost');
  setConnectionStatus('offline');
}

async function checkConnectivity() {
  if (syncStatus === 'syncing') return;
  try {
    // Use a simple HEAD request to root - doesn't require auth
    const response = await fetch('/api/health', {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    // Any response (even 404) means server is reachable
    if (connectionStatus === 'offline') {
      handleOnline();
    }
  } catch {
    if (connectionStatus === 'online') {
      handleOffline();
    }
  }
}

// ============================================================================
// Sync all pending data
// ============================================================================

export async function syncAll(): Promise<{ events: number; images: number; outbox: number; errors: number }> {
  if (syncStatus === 'syncing') {
    console.log('[Sync] Already syncing, skipping');
    return { events: 0, images: 0, outbox: 0, errors: 0 };
  }
  if (!navigator.onLine) {
    console.log('[Sync] Offline, cannot sync');
    return { events: 0, images: 0, outbox: 0, errors: 0 };
  }

  syncStatus = 'syncing';
  setConnectionStatus('syncing');
  let totalErrors = 0;
  let eventsSynced = 0;
  let imagesSynced = 0;
  let outboxSynced = 0;

  try {
    // 1. Sync outbox items (generic mutations)
    const outboxResult = await syncOutbox();
    outboxSynced = outboxResult.synced;
    totalErrors += outboxResult.errors;

    // 2. Sync offline events
    const eventsResult = await syncEvents();
    eventsSynced = eventsResult.synced;
    totalErrors += eventsResult.errors;

    // 3. Sync offline images
    const imagesResult = await syncImages();
    imagesSynced = imagesResult.synced;
    totalErrors += imagesResult.errors;

    // Log the sync
    await addSyncLog({
      syncedAt: Date.now(),
      itemCount: eventsSynced + imagesSynced + outboxSynced,
      errors: totalErrors,
      details: JSON.stringify({
        events: eventsSynced,
        images: imagesSynced,
        outbox: outboxSynced,
        errors: totalErrors,
      }),
    });

    if (eventsSynced + imagesSynced + outboxSynced > 0) {
      console.log(`[Sync] Complete: ${eventsSynced} events, ${imagesSynced} images, ${outboxSynced} outbox items, ${totalErrors} errors`);
    }
  } catch (err) {
    console.error('[Sync] Fatal error:', err);
    totalErrors++;
  } finally {
    syncStatus = 'idle';
    setConnectionStatus(navigator.onLine ? 'online' : 'offline');
  }

  return { events: eventsSynced, images: imagesSynced, outbox: outboxSynced, errors: totalErrors };
}

// ============================================================================
// Sync outbox items
// ============================================================================

async function syncOutbox(): Promise<{ synced: number; errors: number }> {
  const items = await getOutboxItems();
  if (items.length === 0) return { synced: 0, errors: 0 };

  let synced = 0;
  let errors = 0;

  for (const item of items) {
    if (item.attempts >= 5) {
      console.warn('[Sync] Outbox item exceeded max attempts, skipping:', item.id);
      continue;
    }
    try {
      await processOutboxItem(item);
      if (item.id) await markOutboxSynced(item.id);
      synced++;
    } catch (err) {
      console.error('[Sync] Outbox item failed:', item.id, err);
      if (item.id) await updateOutboxAttempt(item.id);
      errors++;
    }
  }

  return { synced, errors };
}

async function processOutboxItem(item: OutboxItem): Promise<void> {
  const payload = JSON.parse(item.payload);

  switch (item.type) {
    case 'event':
      await fetch('/api/trpc/opEvent.create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ json: payload }),
      });
      break;
    case 'status_update':
      await fetch('/api/trpc/agent.updateStatus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ json: payload }),
      });
      break;
    case 'dispatch':
      await fetch('/api/trpc/dispatch.updateStatus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ json: payload }),
      });
      break;
    default:
      console.warn('[Sync] Unknown outbox type:', item.type);
  }
}

// ============================================================================
// Sync offline events
// ============================================================================

async function syncEvents(): Promise<{ synced: number; errors: number }> {
  const events = await getUnsyncedEvents();
  if (events.length === 0) return { synced: 0, errors: 0 };

  let synced = 0;
  let errors = 0;

  for (const event of events) {
    try {
      const response = await fetch('/api/trpc/opEvent.create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': event.idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify({
          json: {
            eventType: event.eventType,
            classification: event.classification,
            sector: event.sector,
            personName: event.personName,
            person2Name: event.person2Name,
            notes: event.notes,
            offlineCreatedAt: event.createdAt,
          },
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const serverId = result?.result?.data?.json?.id || result?.result?.data?.id;
        if (event.localId) {
          await markEventSynced(event.localId, serverId || 0);
        }
        synced++;
      } else {
        errors++;
      }
    } catch (err) {
      console.error('[Sync] Event sync failed:', event.localId, err);
      errors++;
    }
  }

  return { synced, errors };
}

// ============================================================================
// Sync offline images
// ============================================================================

async function syncImages(): Promise<{ synced: number; errors: number }> {
  const images = await getUnsyncedImages();
  if (images.length === 0) return { synced: 0, errors: 0 };

  let synced = 0;
  let errors = 0;

  for (const image of images) {
    try {
      const formData = new FormData();
      formData.append('file', image.blob, `offline-${image.localId}.${image.mimeType.split('/')[1] || 'jpg'}`);
      formData.append('eventLocalId', String(image.eventLocalId));
      formData.append('origin', image.origin);
      formData.append('type', image.type);
      formData.append('createdAt', String(image.createdAt));

      const response = await fetch('/api/trpc/opEvent.uploadOfflineImage', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        if (image.localId) await markImageSynced(image.localId);
        synced++;
      } else {
        errors++;
      }
    } catch (err) {
      console.error('[Sync] Image sync failed:', image.localId, err);
      errors++;
    }
  }

  return { synced, errors };
}

// ============================================================================
// Request Background Sync (if supported)
// ============================================================================

export async function requestBackgroundSync(tag: string = 'ella-sync-events'): Promise<boolean> {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register(tag);
      console.log('[Sync] Background sync registered:', tag);
      return true;
    } catch (err) {
      console.warn('[Sync] Background sync not available:', err);
      return false;
    }
  }
  return false;
}

// ============================================================================
// Periodic cache refresh (sync server data to local cache)
// ============================================================================

export async function refreshLocalCache(): Promise<void> {
  if (!navigator.onLine) return;
  try {
    // Cache persons
    const personsRes = await fetch('/api/trpc/person.list', { credentials: 'include' });
    if (personsRes.ok) {
      const data = await personsRes.json();
      const persons = data?.result?.data?.json || [];
      if (persons.length > 0) {
        const { cachePersons } = await import('./offlineStore');
        await cachePersons(persons.map((p: any) => ({
          id: p.id,
          name: p.name,
          classification: p.classification || 'unknown',
          descriptors: p.faceDescriptor ? JSON.stringify(p.faceDescriptor) : undefined,
          imageUrl: p.imageUrl,
          gender: p.gender,
          cachedAt: Date.now(),
        })));
      }
    }

    // Cache agents
    const agentsRes = await fetch('/api/trpc/agent.list', { credentials: 'include' });
    if (agentsRes.ok) {
      const data = await agentsRes.json();
      const agents = data?.result?.data?.json || [];
      if (agents.length > 0) {
        const { cacheAgents } = await import('./offlineStore');
        await cacheAgents(agents.map((a: any) => ({
          id: a.id,
          name: a.name,
          status: a.status || 'available',
          phone: a.phone,
          sector: a.sector,
          cachedAt: Date.now(),
        })));
      }
    }

    // Cache QR codes (monitor sessions)
    const sessionsRes = await fetch('/api/trpc/session.list', { credentials: 'include' });
    if (sessionsRes.ok) {
      const data = await sessionsRes.json();
      const sessions = data?.result?.data?.json || [];
      if (sessions.length > 0) {
        const { cacheQRCodes } = await import('./offlineStore');
        await cacheQRCodes(sessions.map((s: any) => ({
          code: s.sessionCode || s.code || String(s.id),
          sector: s.sector || s.location || 'default',
          active: s.status === 'active' || s.isActive === true,
          cachedAt: Date.now(),
        })));
      }
    }

    console.log('[Sync] Local cache refreshed');
  } catch (err) {
    console.warn('[Sync] Cache refresh failed (non-blocking):', err);
  }
}
